import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Spinner from '../ui/spinners/Spinner';
import Toast from '../ui/toasts/Toast';
import { ROUTE_BUILDERS } from '../../constants/routes';
import { getScheduleDefinitionHistory } from '../../services/notificationScheduleDefinitionService';
import { getText } from '../../i18n';
import styles from './NotificationScheduleHistory.module.scss';

const rowsFrom = value => {
    if (Array.isArray(value)) return value;
    return (
        value?.items || value?.viewModels || value?.data || value?.history || []
    );
};

const valueFrom = (row, ...keys) =>
    keys
        .map(key => row?.[key])
        .find(value => value !== undefined && value !== null);

const formatDate = value => {
    if (!value) return '—';
    const date = new Date(value);
    return Number.isNaN(date.getTime())
        ? String(value)
        : new Intl.DateTimeFormat(undefined, {
              dateStyle: 'medium',
              timeStyle: 'short',
              timeZone: 'UTC',
          }).format(date) + ' UTC';
};

const NotificationScheduleHistory = () => {
    const { notificationId, scheduleDefinitionId } = useParams();
    const navigate = useNavigate();
    const [history, setHistory] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);

    const loadHistory = async isActive => {
        setIsLoading(true);
        setLoadError(false);
        try {
            const response = await getScheduleDefinitionHistory(
                null,
                notificationId,
                scheduleDefinitionId,
            );
            if (isActive()) setHistory(rowsFrom(response));
        } catch (error) {
            console.error('Failed to load schedule-definition history', error);
            if (isActive()) {
                setHistory([]);
                setLoadError(true);
                Toast.error(getText('msg_ddef544a92db'));
            }
        } finally {
            if (isActive()) setIsLoading(false);
        }
    };

    useEffect(() => {
        let mounted = true;
        loadHistory(() => mounted);
        return () => {
            mounted = false;
        };
    }, [notificationId, scheduleDefinitionId]);

    if (isLoading)
        return <Spinner width={50} className={styles.loadingSpinner} />;

    return (
        <div className="page-content">
            <div className={`card ${styles.card}`}>
                <div className="card-body">
                    <div className={styles.header}>
                        <div>
                            <p className="page-title card-title mb-1">
                                {getText('msg_69eb06eee8db')}
                            </p>
                            <p className={styles.subtitle}>
                                {getText('msg_9713e5efbd9f')}
                            </p>
                        </div>
                        <button
                            className="button-base ing-button-secondary-left"
                            type="button"
                            onClick={() =>
                                navigate(
                                    ROUTE_BUILDERS.notificationScheduleList(
                                        notificationId,
                                    ),
                                )
                            }
                        >
                            {getText('msg_62ec39260331')}
                        </button>
                    </div>
                    {loadError ? (
                        <div className={styles.errorState} role="alert">
                            <p>{getText('msg_ddef544a92db')}</p>
                            <button
                                className="button-base ing-button-primary-left"
                                type="button"
                                onClick={() => loadHistory(() => true)}
                            >
                                {getText('msg_042c862e4467')}
                            </button>
                        </div>
                    ) : history.length === 0 ? (
                        <div className={styles.emptyState}>
                            {getText('msg_fa3737ed0c5f')}
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table
                                className={`table align-middle ${styles.table}`}
                            >
                                <thead>
                                    <tr>
                                        <th>{getText('msg_2da600bf9404')}</th>
                                        <th>{getText('msg_709a23220f2c')}</th>
                                        <th>{getText('msg_89836a870e44')}</th>
                                        <th>{getText('msg_bae7d5be7082')}</th>
                                        <th>{getText('msg_160fdedf5c4e')}</th>
                                        <th>{getText('msg_7fb0771854a5')}</th>
                                        <th>{getText('msg_f52555e24ee3')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {history.map((row, index) => {
                                        const occurrences =
                                            valueFrom(
                                                row,
                                                'notificationSchedules',
                                                'schedules',
                                                'scheduledFor',
                                            ) || [];
                                        return (
                                            <tr
                                                key={`${valueFrom(row, 'id', 'notificationScheduleDefinitionId') || 'version'}-${index}`}
                                            >
                                                <td>{index + 1}</td>
                                                <td className={styles.nameCell}>
                                                    {valueFrom(row, 'name') ||
                                                        '—'}
                                                </td>
                                                <td>
                                                    {valueFrom(
                                                        row,
                                                        'frequencyName',
                                                        'notificationFrequencyName',
                                                        'frequency',
                                                    ) || '—'}
                                                </td>
                                                <td>
                                                    {valueFrom(
                                                        row,
                                                        'statusName',
                                                        'status',
                                                        'isEnabled',
                                                    ) ?? '—'}
                                                </td>
                                                <td>
                                                    {formatDate(
                                                        valueFrom(
                                                            row,
                                                            'validFrom',
                                                            'periodStart',
                                                            'sysStartTime',
                                                            'createdAt',
                                                        ),
                                                    )}
                                                </td>
                                                <td>
                                                    {formatDate(
                                                        valueFrom(
                                                            row,
                                                            'validTo',
                                                            'periodEnd',
                                                            'sysEndTime',
                                                            'updatedAt',
                                                        ),
                                                    )}
                                                </td>
                                                <td>
                                                    {Array.isArray(occurrences)
                                                        ? occurrences
                                                              .map(formatDate)
                                                              .join(', ') || '—'
                                                        : formatDate(
                                                              occurrences,
                                                          )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default NotificationScheduleHistory;
